import React, { useState, useEffect, useCallback } from 'react';
import { 
  Globe, Save, RefreshCw, Loader, Plus, X, Building2, Trash2,
  Sparkles, BookOpen, MapPin, Tag, ShieldX, Lightbulb, CheckCircle
} from 'lucide-react';
import { apiCall } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useToast } from './Toast';

export default function BusinessProfile({ selectedAccount }) {
  const { theme } = useTheme();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState('manual');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [learning, setLearning] = useState(false);

  // Profile fields
  const [businessName, setBusinessName] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [coreServices, setCoreServices] = useState([]);
  const [coreKeywords, setCoreKeywords] = useState([]);
  const [negativeThemes, setNegativeThemes] = useState([]);
  const [targetLocations, setTargetLocations] = useState([]);
  const [usps, setUsps] = useState([]);

  // Learned data
  const [learnedKeywords, setLearnedKeywords] = useState([]);
  const [learnedSources, setLearnedSources] = useState({});
  const [lastScraped, setLastScraped] = useState(null);
  const [lastLearned, setLastLearned] = useState(null);

  // Input helpers
  const [newService, setNewService] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [newNegative, setNewNegative] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newUsp, setNewUsp] = useState('');

  const fetchProfile = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const response = await apiCall('/api/business-profile');
      const data = await response.json();
      if (data.success && data.profile) {
        const p = data.profile;
        setBusinessName(p.business_name || '');
        setBusinessDescription(p.business_description || '');
        setWebsiteUrl(p.website_url || '');
        setIndustry(p.industry || '');
        setTargetAudience(p.target_audience || '');
        setCoreServices(p.core_services || []);
        setCoreKeywords(p.core_keywords || []);
        setNegativeThemes(p.negative_themes || []);
        setTargetLocations(p.target_locations || []);
        setUsps(p.usps || []);
        setLearnedKeywords(p.learned_keywords || []);
        setLastScraped(p.last_scraped_at);
        setLastLearned(p.last_learned_at);
        setActiveTab('manual');
      } else {
        setActiveTab('autodetect');
      }
    } catch (err) {
      console.error('Failed to load business profile:', err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiCall('/api/business-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: businessName,
          business_description: businessDescription,
          website_url: websiteUrl,
          industry,
          target_audience: targetAudience,
          core_services: coreServices,
          core_keywords: coreKeywords,
          negative_themes: negativeThemes,
          target_locations: targetLocations,
          usps
        })
      });
      const data = await response.json();
      if (data.success) {
        showToast('Business profile saved', 'success');
      } else {
        throw new Error(data.error || 'Failed to save');
      }
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoDetect = async () => {
    if (!websiteUrl.trim()) {
      showToast('Enter a website URL first', 'error');
      return;
    }
    setDetecting(true);
    try {
      const response = await apiCall('/api/business-profile/auto-detect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 90000,
        body: JSON.stringify({ websiteUrl: websiteUrl.trim() })
      });
      const data = await response.json();
      if (data.success && data.profile) {
        const p = data.profile;
        setBusinessName(p.business_name || businessName);
        setBusinessDescription(p.business_description || businessDescription);
        setIndustry(p.industry || industry);
        setTargetAudience(p.target_audience || targetAudience);
        setCoreServices(p.core_services || []);
        setCoreKeywords(p.core_keywords || []);
        setNegativeThemes(p.negative_themes || []);
        setTargetLocations(p.target_locations || []);
        setUsps(p.usps || []);
        setLastScraped(new Date().toISOString());
        showToast('Website analyzed with AI - profile updated', 'success');
        setActiveTab('manual');
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err) {
      showToast(`Auto-detect failed: ${err.message}`, 'error');
    } finally {
      setDetecting(false);
    }
  };

  const handleLearnFromCampaigns = async () => {
    setLearning(true);
    try {
      const response = await apiCall('/api/business-profile/learn-from-campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 60000
      });
      const data = await response.json();
      if (data.success) {
        setLearnedKeywords(data.learnedKeywords || []);
        setLearnedSources(data.sources || {});
        setLastLearned(new Date().toISOString());
        showToast(`Learned ${(data.learnedKeywords || []).length} keywords from campaigns`, 'success');
      } else {
        throw new Error(data.error || 'Learning failed');
      }
    } catch (err) {
      showToast(`Failed: ${err.message}`, 'error');
    } finally {
      setLearning(false);
    }
  };

  const handleReset = () => {
    if (!window.confirm('Are you sure you want to reset the entire profile? This cannot be undone.')) return;
    setBusinessName('');
    setBusinessDescription('');
    setWebsiteUrl('');
    setIndustry('');
    setTargetAudience('');
    setCoreServices([]);
    setCoreKeywords([]);
    setNegativeThemes([]);
    setTargetLocations([]);
    setUsps([]);
    setLearnedKeywords([]);
    setLastScraped(null);
    setLastLearned(null);
    showToast('Profile reset. Click Save to confirm, or re-scan your website.', 'info');
  };

  const addToList = (list, setList, value, setValue) => {
    const trimmed = value.trim();
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setValue('');
  };

  const removeFromList = (list, setList, index) => {
    setList(list.filter((_, i) => i !== index));
  };

  const styles = {
    container: { padding: '32px', maxWidth: 1000, margin: '0 auto' },
    header: { marginBottom: 24 },
    title: { fontSize: 28, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 8 },
    subtitle: { fontSize: 14, color: theme.colors.text.secondary },
    tabs: { display: 'flex', gap: 0, marginBottom: 24, borderBottom: `1px solid ${theme.colors.border.primary}` },
    tab: (active) => ({
      padding: '12px 24px', fontSize: 14, fontWeight: 600,
      color: active ? '#6366f1' : theme.colors.text.secondary,
      borderBottom: active ? '2px solid #6366f1' : '2px solid transparent',
      cursor: 'pointer', background: 'none', border: 'none',
      display: 'flex', alignItems: 'center', gap: 8
    }),
    card: {
      background: theme.colors.bg.secondary, borderRadius: 12,
      border: `1px solid ${theme.colors.border.primary}`, padding: 24, marginBottom: 20
    },
    label: { fontSize: 13, fontWeight: 600, color: theme.colors.text.primary, marginBottom: 6, display: 'block' },
    input: {
      width: '100%', padding: '10px 14px', borderRadius: 8,
      border: `1px solid ${theme.colors.border.primary}`, background: theme.colors.bg.primary,
      color: theme.colors.text.primary, fontSize: 14, outline: 'none', boxSizing: 'border-box'
    },
    textarea: {
      width: '100%', padding: '10px 14px', borderRadius: 8, minHeight: 80, resize: 'vertical',
      border: `1px solid ${theme.colors.border.primary}`, background: theme.colors.bg.primary,
      color: theme.colors.text.primary, fontSize: 14, outline: 'none', boxSizing: 'border-box'
    },
    chipContainer: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
    chip: {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
      borderRadius: 20, background: '#6366f115', color: '#6366f1',
      fontSize: 13, fontWeight: 500
    },
    chipRemove: {
      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
      color: '#6366f1', opacity: 0.6, display: 'flex'
    },
    addRow: { display: 'flex', gap: 8, marginTop: 8 },
    addInput: {
      flex: 1, padding: '8px 12px', borderRadius: 8,
      border: `1px solid ${theme.colors.border.primary}`, background: theme.colors.bg.primary,
      color: theme.colors.text.primary, fontSize: 13, outline: 'none'
    },
    addBtn: {
      padding: '8px 12px', borderRadius: 8, border: 'none', background: '#6366f1',
      color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13
    },
    button: (variant = 'default') => ({
      padding: '10px 20px', borderRadius: 8,
      border: variant === 'default' ? `1px solid ${theme.colors.border.primary}` : variant === 'danger' ? 'none' : 'none',
      background: variant === 'primary' ? '#6366f1' : variant === 'success' ? '#10b981' : variant === 'danger' ? '#ef4444' : theme.colors.bg.secondary,
      color: variant === 'default' ? theme.colors.text.primary : '#fff',
      fontSize: 14, fontWeight: 500, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 8
    }),
    section: { marginBottom: 24 },
    fieldGroup: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }
  };

  const ChipList = ({ items, setItems, placeholder, value, setValue, icon: Icon, color = '#6366f1' }) => (
    <div>
      <div style={styles.chipContainer}>
        {items.map((item, i) => (
          <span key={i} style={{ ...styles.chip, background: `${color}15`, color }}>
            {item}
            <button style={styles.chipRemove} onClick={() => removeFromList(items, setItems, i)}>
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div style={styles.addRow}>
        <input
          style={styles.addInput}
          placeholder={placeholder}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addToList(items, setItems, value, setValue); } }}
        />
        <button style={styles.addBtn} onClick={() => addToList(items, setItems, value, setValue)}>
          <Plus size={14} /> Add
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: 80, color: theme.colors.text.secondary }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
          <p>Loading business profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Business Profile</h1>
        <p style={styles.subtitle}>
          Define your business context to improve AI recommendations across all modules
        </p>
      </div>

      <div style={styles.tabs}>
        <button style={styles.tab(activeTab === 'manual')} onClick={() => setActiveTab('manual')}>
          <Building2 size={16} /> Manual
        </button>
        <button style={styles.tab(activeTab === 'autodetect')} onClick={() => setActiveTab('autodetect')}>
          <Globe size={16} /> Auto-detect
        </button>
        <button style={styles.tab(activeTab === 'learned')} onClick={() => setActiveTab('learned')}>
          <BookOpen size={16} /> Learned
        </button>
      </div>

      {activeTab === 'manual' && (
        <>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#6366f115', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={20} color="#6366f1" />
              </div>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Business Information</h3>
                <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: 0 }}>Basic details about the business</p>
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <div>
                <label style={styles.label}>Business Name</label>
                <input style={styles.input} value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. Bidoryx" />
              </div>
              <div>
                <label style={styles.label}>Industry</label>
                <input style={styles.input} value={industry} onChange={e => setIndustry(e.target.value)} placeholder="e.g. SaaS, Marketing Technology" />
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={styles.label}>Business Description</label>
              <textarea style={styles.textarea} value={businessDescription} onChange={e => setBusinessDescription(e.target.value)} placeholder="Describe what the business does, its main services, and target market..." />
            </div>

            <div style={{ ...styles.fieldGroup, marginTop: 16 }}>
              <div>
                <label style={styles.label}>Website URL</label>
                <input style={styles.input} value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://www.example.com" />
              </div>
              <div>
                <label style={styles.label}>Target Audience</label>
                <input style={styles.input} value={targetAudience} onChange={e => setTargetAudience(e.target.value)} placeholder="e.g. E-commerce owners, marketing managers" />
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Tag size={18} color="#10b981" />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Core Services & Products</h3>
            </div>
            <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginBottom: 8 }}>
              Search terms matching these will always be considered relevant
            </p>
            <ChipList items={coreServices} setItems={setCoreServices} placeholder="e.g. Google Ads optimization, bid management..." value={newService} setValue={setNewService} icon={Tag} color="#10b981" />
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Sparkles size={18} color="#6366f1" />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Core Keywords</h3>
            </div>
            <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginBottom: 8 }}>
              Keywords that are central to this business (used for relevance scoring)
            </p>
            <ChipList items={coreKeywords} setItems={setCoreKeywords} placeholder="e.g. google ads tool, bid optimizer, ppc automation..." value={newKeyword} setValue={setNewKeyword} icon={Sparkles} />
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <ShieldX size={18} color="#ef4444" />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Irrelevant Themes</h3>
            </div>
            <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginBottom: 8 }}>
              Search terms matching these will be flagged as irrelevant more aggressively
            </p>
            <ChipList items={negativeThemes} setItems={setNegativeThemes} placeholder="e.g. free tools, DIY, manual bidding..." value={newNegative} setValue={setNewNegative} icon={ShieldX} color="#ef4444" />
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <MapPin size={18} color="#f59e0b" />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Target Locations</h3>
            </div>
            <ChipList items={targetLocations} setItems={setTargetLocations} placeholder="e.g. Nederland, België, DACH..." value={newLocation} setValue={setNewLocation} icon={MapPin} color="#f59e0b" />
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Lightbulb size={18} color="#8b5cf6" />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>USPs (Unique Selling Points)</h3>
            </div>
            <ChipList items={usps} setItems={setUsps} placeholder="e.g. AI-powered, saves 10+ hours/week..." value={newUsp} setValue={setNewUsp} icon={Lightbulb} color="#8b5cf6" />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button style={styles.button('danger')} onClick={handleReset}>
              <Trash2 size={16} /> Reset Profile
            </button>
            <button style={styles.button('primary')} onClick={handleSave} disabled={saving}>
              {saving ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </>
      )}

      {activeTab === 'autodetect' && (
        <>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#10b98115', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Globe size={20} color="#10b981" />
              </div>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Auto-detect from Website</h3>
                <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: 0 }}>
                  We'll scrape your website and use AI to extract business context
                </p>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Website URL</label>
              <div style={{ display: 'flex', gap: 12 }}>
                <input
                  style={{ ...styles.input, flex: 1 }}
                  value={websiteUrl}
                  onChange={e => setWebsiteUrl(e.target.value)}
                  placeholder="https://www.example.com"
                />
                <button style={styles.button('primary')} onClick={handleAutoDetect} disabled={detecting}>
                  {detecting ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
                  {detecting ? 'Analyzing...' : 'Analyze Website'}
                </button>
              </div>
            </div>

            {lastScraped && (
              <p style={{ fontSize: 12, color: theme.colors.text.secondary }}>
                Last scanned: {new Date(lastScraped).toLocaleString()}
              </p>
            )}

            {detecting && (
              <div style={{ textAlign: 'center', padding: 40, color: theme.colors.text.secondary }}>
                <Loader size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
                <p style={{ fontSize: 14 }}>Scraping website and analyzing with AI...</p>
                <p style={{ fontSize: 12 }}>This can take 10-20 seconds</p>
              </div>
            )}
          </div>

          {(coreServices.length > 0 || coreKeywords.length > 0 || targetLocations.length > 0) && !detecting && (
            <div style={styles.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <CheckCircle size={18} color="#10b981" />
                <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Detected Profile</h3>
              </div>

              {businessName && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary }}>Business:</span>
                  <span style={{ fontSize: 14, color: theme.colors.text.primary, marginLeft: 8 }}>{businessName}</span>
                </div>
              )}
              {industry && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary }}>Industry:</span>
                  <span style={{ fontSize: 14, color: theme.colors.text.primary, marginLeft: 8 }}>{industry}</span>
                </div>
              )}
              {coreServices.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary, display: 'block', marginBottom: 6 }}>Services:</span>
                  <div style={styles.chipContainer}>
                    {coreServices.map((s, i) => <span key={i} style={{ ...styles.chip, background: '#10b98115', color: '#10b981' }}>{s}</span>)}
                  </div>
                </div>
              )}
              {coreKeywords.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary, display: 'block', marginBottom: 6 }}>Keywords:</span>
                  <div style={styles.chipContainer}>
                    {coreKeywords.slice(0, 20).map((k, i) => <span key={i} style={styles.chip}>{k}</span>)}
                    {coreKeywords.length > 20 && <span style={{ fontSize: 12, color: theme.colors.text.secondary }}>+{coreKeywords.length - 20} more</span>}
                  </div>
                </div>
              )}
              {targetLocations.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text.secondary, display: 'block', marginBottom: 6 }}>Locations:</span>
                  <div style={styles.chipContainer}>
                    {targetLocations.map((l, i) => <span key={i} style={{ ...styles.chip, background: '#f59e0b15', color: '#f59e0b' }}>{l}</span>)}
                  </div>
                </div>
              )}

              <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 16 }}>
                Switch to the "Manual" tab to review and edit these values, then click Save.
              </p>
            </div>
          )}
        </>
      )}

      {activeTab === 'learned' && (
        <>
          <div style={styles.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: '#8b5cf615', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BookOpen size={20} color="#8b5cf6" />
              </div>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>Learn from Campaigns</h3>
                <p style={{ fontSize: 12, color: theme.colors.text.secondary, margin: 0 }}>
                  Automatically extract business keywords from your campaign data and converting search terms
                </p>
              </div>
            </div>

            <button style={styles.button('primary')} onClick={handleLearnFromCampaigns} disabled={learning}>
              {learning ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={16} />}
              {learning ? 'Analyzing campaigns...' : 'Learn from Campaign Data'}
            </button>

            {lastLearned && (
              <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginTop: 12 }}>
                Last learned: {new Date(lastLearned).toLocaleString()}
              </p>
            )}
          </div>

          {learnedKeywords.length > 0 && (
            <div style={styles.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Sparkles size={18} color="#8b5cf6" />
                <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.colors.text.primary, margin: 0 }}>
                  Learned Keywords ({learnedKeywords.length})
                </h3>
              </div>
              <p style={{ fontSize: 12, color: theme.colors.text.secondary, marginBottom: 12 }}>
                These keywords were automatically extracted from your campaigns, ad groups, and converting search terms.
                They are used alongside your manual keywords for relevance scoring.
              </p>
              <div style={styles.chipContainer}>
                {learnedKeywords.slice(0, 50).map((kw, i) => (
                  <span key={i} style={{ ...styles.chip, background: '#8b5cf615', color: '#8b5cf6' }}>{kw}</span>
                ))}
                {learnedKeywords.length > 50 && (
                  <span style={{ fontSize: 12, color: theme.colors.text.secondary, padding: '6px 12px' }}>
                    +{learnedKeywords.length - 50} more
                  </span>
                )}
              </div>

              {learnedSources && Object.keys(learnedSources).length > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: theme.colors.bg.primary, borderRadius: 8, fontSize: 12, color: theme.colors.text.secondary }}>
                  <strong>Sources:</strong>
                  {learnedSources.campaignKeywords?.length > 0 && <span> Campaign keywords ({learnedSources.campaignKeywords.length})</span>}
                  {learnedSources.convertingSearchTerms?.length > 0 && <span> · Converting terms ({learnedSources.convertingSearchTerms.length})</span>}
                  {learnedSources.campaignNames?.length > 0 && <span> · Campaign names ({learnedSources.campaignNames.length})</span>}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
